import React, { useEffect, useRef } from "react";
import { map } from "lodash";
import { Spring } from "react-spring/renderprops";
import { List } from "react-virtualized";
import styled from "styled-components";
import { content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { usePages, useSize } from "@lkf/hooks";
import PostComponent from "@radarly/components/post/widget-post";
import { Post } from "@radarly/models/posts";
import { Project } from "@radarly/models/project";
import Footer from "./footer";
import useTopPosts from "./useTopPosts";

interface IProps {
  posts: Post[];
  total?: number;
  project?: Project;
  locale: Locale;
  timezone: Timezone;
  onClick?: () => void;
  closeFullscreen: () => void;
  isFullscreen?: boolean;
}

const ROW_HEIGHT = 90;
const LIMIT = 3;

const PostsContainer = styled.div<{
  height: string;
  hasBorderBottom?: boolean;
}>`
  max-height: ${(props) => props.height};
  overflow: hidden !important;
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  border-bottom: ${(props) =>
    props.hasBorderBottom && `1px solid ${content(props, "tertiary")}`};
  flex-grow: 1;
`;

const RadarlyCardsPostsTopPosts = ({
  posts,
  project,
  locale,
  timezone,
  onClick,
  closeFullscreen,
  isFullscreen = false,
}: IProps) => {
  const ref = useRef();
  const { width } = useSize(ref);
  const isNarrow = width < 600;
  const { postsList, paginationTotal, onClickPost } = useTopPosts(
    posts,
    LIMIT,
    isFullscreen,
    closeFullscreen
  );
  const showPagination = !isFullscreen && paginationTotal > LIMIT;
  const { page, onPageReset, onPageUp, onPageDown } = usePages(
    paginationTotal,
    LIMIT
  );

  useEffect(() => {
    onPageReset();
  }, [onPageReset, posts]);

  const postRowRenderer = ({
    key,
    index,
    style,
    isScrolling,
  }: {
    key: string;
    index: number;
    style: any;
    isScrolling?: boolean;
  }) => {
    const post: Post & { isPhantom?: boolean } = postsList[index];
    if (post.isPhantom) {
      return <div style={{ height: ROW_HEIGHT + 1 }} />;
    }
    return (
      <PostComponent
        key={key}
        style={style}
        post={post}
        project={project}
        locale={locale}
        timezone={timezone}
        isNarrow={isNarrow}
        width={width}
        isTooltipDisabled={isScrolling}
        onClick={onClickPost}
      />
    );
  };

  return (
    <div ref={ref}>
      <PostsContainer
        height={isFullscreen ? "100%" : `${ROW_HEIGHT * LIMIT}px`}
        hasBorderBottom={paginationTotal >= LIMIT}
      >
        {isFullscreen ? (
          map(posts, (post, i) => (
            <PostComponent
              key={i}
              post={post}
              project={project}
              locale={locale}
              timezone={timezone}
              width={width}
              isNarrow={isNarrow}
              onClick={onClickPost}
            />
          ))
        ) : (
          <Spring from={{ top: 0 }} to={{ top: ROW_HEIGHT * (page.from - 1) }}>
            {(props) => (
              <List
                width={1}
                height={ROW_HEIGHT * LIMIT}
                rowCount={postsList.length}
                rowHeight={ROW_HEIGHT}
                rowRenderer={postRowRenderer}
                containerStyle={{ width: "100%", maxWidth: "100%" }}
                style={{ width: "100%", overflow: "hidden" }}
                scrollTop={props.top}
              />
            )}
          </Spring>
        )}
      </PostsContainer>
      {!isFullscreen && onClick && (
        <Footer
          showPagination={showPagination}
          totalPosts={paginationTotal}
          onPageDown={onPageDown}
          onPageUp={onPageUp}
          onClickView={onClick}
          page={page}
          locale={locale}
        />
      )}
    </div>
  );
};

export default RadarlyCardsPostsTopPosts;
